import React, { useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Grid,
  Step,
  StepLabel,
  Stepper,
  Typography,
  TextField,
  Switch,
  FormControlLabel,
  LinearProgress,
  Alert,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import GmailIcon from "@mui/icons-material/Mail";
import { useNavigate } from "react-router-dom";

// Styled Components
const WelcomeContainer = styled(Box)(({ theme }) => ({
  minHeight: "100vh",
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  background: "#0F0E1A",
  color: "#fff",
  padding: theme.spacing(3),
}));

const StepContainer = styled(Box)(({ theme }) => ({
  minHeight: "100vh",
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  background: "linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)",
  color: "#fff",
  padding: theme.spacing(3),
}));

const ContentBox = styled(Box)(({ theme }) => ({
  textAlign: "center",
  maxWidth: 700,
  width: "100%",
}));

const LogoText = styled(Typography)(({ theme }) => ({
  fontSize: "2.4rem",
  fontWeight: "400",
  color: "rgba(255, 255, 255, 0.9)",
  marginBottom: theme.spacing(2),
  "& .spring": {
    color: "#3563AF",
    fontWeight: "bold",
  },
}));

const EmailOptionCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  cursor: "pointer",
  transition: "all 0.3s ease",
  background: "transparent",
  boxShadow: "none",
  width: "100%",
  maxWidth: 500,
  margin: "auto",
}));

const UploadArea = styled(Box)(({ theme }) => ({
  border: "2px dashed rgba(255, 255, 255, 0.7)",
  borderRadius: theme.spacing(2),
  padding: theme.spacing(4),
  textAlign: "center",
  cursor: "pointer",
  transition: "all 0.3s ease",
  background: "transparent",
  width: "100%",
  maxWidth: 450,
  margin: "auto",
}));

const ActionButton = styled(Button)(({ theme }) => ({
  marginTop: theme.spacing(2),
  marginRight: theme.spacing(1),
  padding: theme.spacing(1, 2),
  fontSize: "1rem",
  fontWeight: "bold",
  borderRadius: theme.spacing(1),
  textTransform: "none !important",
  fontWeight: "400 !important",
  "&.continue-btn": {
    background: "rgba(255, 255, 255, 0.2)",
    color: "#fff",
    "&:hover": {
      background: "#000",
      border: "1px solid rgba(255, 255, 255, 0.2)",
    },
  },
  "&.back-btn": {
    background: "rgba(255, 255, 255, 0.1)",
    color: "#fff",
    "&:hover": {
      background: "#000",
      border: "1px solid rgba(255, 255, 255, 0.2)",
    },
  },
}));

const IconSwitch = styled(Switch)(({ theme }) => ({
  width: 44,
  height: 24,
  padding: 0,

  "& .MuiSwitch-switchBase": {
    padding: 2,
    transitionDuration: "250ms",

    "&.Mui-checked": {
      transform: "translateX(20px)",

      "& .MuiSwitch-thumb:before": {
        content: '"✓"',
        color: "#2e7d32",
      },

      "& + .MuiSwitch-track": {
        backgroundColor: "#4caf50",
        opacity: 1,
      },
    },
  },

  "& .MuiSwitch-thumb": {
    width: 20,
    height: 20,
    backgroundColor: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",

    "&:before": {
      content: '"✕"',
      fontSize: "12px",
      fontWeight: "bold",
      color: "#9e9e9e", 
      lineHeight: 1,
    },
  },

  "& .MuiSwitch-track": {
    borderRadius: 12,
    backgroundColor: "#bdbdbd",
    opacity: 1,
  },
}));

const OnboardingPage = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [email, setEmail] = useState("");
  const [gmailConnected, setGmailConnected] = useState(false);
  const [outlookConnected, setOutlookConnected] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [hiddenPersonalDomains, setHiddenPersonalDomains] = useState(true);
  const [markContactsPrivate, setMarkContactsPrivate] = useState(false);

  const steps = [
    "Welcome",
    "Connect Email",
    "Upload CRM Data",
    "Privacy Settings",
  ];

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleGmailConnect = () => {
    setGmailConnected(!gmailConnected);
  };

  const handleOutlookConnect = () => {
    setOutlookConnected(!outlookConnected);
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file && file.name.endsWith(".csv")) {
      setUploadedFile(file);
    } else {
      alert("Please upload a valid CSV file");
    }
  };

  const handleFinish = () => {
    console.log("Onboarding completed");
    navigate("/new-spring-dashboard");
    // Handle finish logic here
  };

  // Step 1: Welcome
  const renderWelcomeStep = () => (
    <WelcomeContainer>
      <ContentBox>
        <LogoText>
          NEW<span className="spring">SPRING</span>
        </LogoText>
        <Typography
          variant="h5"
          sx={{ marginBottom: 2, color: "rgba(255, 255, 255, 0.9)" }}
        >
          Step 1 of 4
        </Typography>
        <Typography
          variant="h3"
          sx={{ marginBottom: 2, color: "rgba(255, 255, 255, 0.9)" }}
        >
          Welcome, Partner!
        </Typography>
        <Typography
          variant="h6"
          sx={{
            marginBottom: 1,
            lineHeight: 1.6,
            color: "rgba(255, 255, 255, 0.9)",
          }}
        >
          Let's get your Lucra Relationship Intelligence Engine Setup.
        </Typography>
        <Typography
          variant="body1"
          sx={{ marginBottom: 2, color: "rgba(255, 255, 255, 0.8)" }}
        >
          This short wizard will guide you through connecting your data sources
          and configuring your preferences to maximize your LRIE experience.
          It's quick easy, and essential for powerful insights
        </Typography>
        <ActionButton
          className="continue-btn"
          onClick={handleNext}
          size="large"
        >
          Continue
        </ActionButton>
      </ContentBox>
    </WelcomeContainer>
  );

  // Step 2: Connect Email
  const renderEmailStep = () => (
    <StepContainer>
      <ContentBox>
        <LogoText>
          NEW<span className="spring">SPRING</span>
        </LogoText>
        <Typography
          variant="h5"
          sx={{ marginBottom: 2, color: "rgba(255, 255, 255, 0.9)" }}
        >
          Step 2 of 4
        </Typography>
        <Typography
          variant="h3"
          sx={{
            marginBottom: 3,
            color: "rgba(255, 255, 255, 0.9)",
            fontWeight: "400",
          }}
        >
          Connect Your Email Account
        </Typography>
        <Typography
          variant="h6"
          sx={{ marginBottom: 4, color: "rgba(255, 255, 255, 0.8)" }}
        >
          Connect your primary work email to enable seamless data
          synchronization and connection mapping within LRIE
        </Typography>

        {/* Gmail */}
        <EmailOptionCard>
          <CardContent
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: 2,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <GmailIcon sx={{ fontSize: "2rem", color: "#ea4335" }} />
              <Box sx={{ textAlign: "left" }}>
                <Typography
                  variant="body1"
                  sx={{ color: "rgba(255, 255, 255, 0.8)" }}
                >
                  Gmail
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: gmailConnected ? "#4caf50" : "#EF8A00",
                  }}
                >
                  {gmailConnected ? "Connected" : "Disconnected"}
                </Typography>
              </Box>
            </Box>
            <ActionButton
              className="continue-btn"
              onClick={handleGmailConnect}
              size="small"
            >
              {gmailConnected ? "Disconnect" : "Connect"}
            </ActionButton>
          </CardContent>
        </EmailOptionCard>

        {/* Outlook */}
        <EmailOptionCard>
          <CardContent
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: 2,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Box
                sx={{
                  fontSize: "2rem",
                  fontWeight: "bold",
                  color: "#0078d4",
                }}
              >
                Ⓞ
              </Box>
              <Box sx={{ textAlign: "left" }}>
                <Typography
                  variant="body1"
                  sx={{ color: "rgba(255, 255, 255, 0.8)" }}
                >
                  Outlook 365
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: outlookConnected ? "#4caf50" : "#EF8A00",
                  }}
                >
                  {outlookConnected ? "Connected" : "Disconnected"}
                </Typography>
              </Box>
            </Box>
            <ActionButton
              className="continue-btn"
              onClick={handleOutlookConnect}
              size="small"
            >
              {outlookConnected ? "Disconnect" : "Connect"}
            </ActionButton>
          </CardContent>
        </EmailOptionCard>

        <Box sx={{ marginTop: 2, marginBottom: 2 }}>
          <Typography variant="h6" sx={{ color: "rgba(255, 255, 255, 0.8)" }}>
            If you encounter issues connecting please ensure you've granted the
            necessary permissions or contact support for assistance.
          </Typography>
        </Box>

        <Typography
          variant="body2"
          sx={{ color: "#EF8A00", cursor: "pointer", marginBottom: 2 }}
        >
          Access Help Center
        </Typography>

        <Box sx={{ display: "flex", gap: 2, justifyContent: "center" }}>
          <ActionButton className="back-btn" onClick={handleBack}>
            Back
          </ActionButton>
          <ActionButton className="continue-btn" onClick={handleNext}>
            Continue
          </ActionButton>
        </Box>
      </ContentBox>
    </StepContainer>
  );

  // Step 3: Upload CRM Data
  const renderUploadStep = () => (
    <StepContainer>
      <ContentBox>
        <LogoText>
          NEW<span className="spring">SPRING</span>
        </LogoText>
        <Typography
          variant="h5"
          sx={{ marginBottom: 2, color: "rgba(255, 255, 255, 0.9)" }}
        >
          Step 3 of 4
        </Typography>
        <Typography
          variant="h3"
          sx={{ marginBottom: 3, color: "rgba(255, 255, 255, 0.9)" }}
        >
          Upload CRM Data
        </Typography>
        <Typography
          variant="h6"
          sx={{ marginBottom: 4, color: "rgba(255, 255, 255, 0.8)" }}
        >
          Integrate your CRM to enhance relations insights (Optional)
        </Typography>

        <input
          type="file"
          accept=".csv"
          onChange={handleFileUpload}
          style={{ display: "none" }}
          id="file-upload"
        />
        <label htmlFor="file-upload" style={{ width: "100%" }}>
          <UploadArea component="div">
            <CloudUploadIcon
              sx={{ width: 50, height: 50, color: "#00bcd4", marginBottom: 1 }}
            />
            <Typography variant="h6" sx={{ marginBottom: 1 }}>
              Drag & Drop your CRM csv here
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: "rgba(255, 255, 255, 0.8)" }}
            >
              or click to select file
            </Typography>
          </UploadArea>
        </label>

        {uploadedFile && (
          <Alert severity="success" sx={{ marginTop: 2 }}>
            File selected: {uploadedFile.name}
          </Alert>
        )}

        {!uploadedFile && (
          <Typography
            variant="body2"
            sx={{
              marginTop: 2,
              color: "rgba(255, 255, 255, 0.8)",
              marginBottom: 4,
            }}
          >
            No file selected.
          </Typography>
        )}

        <Box
          sx={{
            display: "flex",
            gap: 2,
            justifyContent: "space-between",
            alignItems: "center",
            marginTop: 4,
            width: "100%",
            maxWidth: 400,
            margin: "auto",
          }}
        >
          {/* <ActionButton className="back-btn" onClick={handleBack}>
            Back
          </ActionButton> */}
          <ActionButton className="back-btn" onClick={handleNext}>
            Skip for Now
          </ActionButton>
          <ActionButton className="continue-btn" onClick={handleNext}>
            Continue
          </ActionButton>
        </Box>
      </ContentBox>
    </StepContainer>
  );

  // Step 4: Privacy Settings
  const renderPrivacyStep = () => (
    <StepContainer>
      <ContentBox>
        <LogoText>
          NEW<span className="spring">SPRING</span>
        </LogoText>
        <Typography
          variant="h5"
          sx={{ marginBottom: 2, color: "rgba(255, 255, 255, 0.9)" }}
        >
          Step 4 of 4
        </Typography>
        <Typography
          variant="h3"
          sx={{ marginBottom: 4, color: "rgba(255, 255, 255, 0.9)" }}
        >
          Privacy settings
        </Typography>

        <Card
          sx={{
            background: "none",
            boxShadow: "none",
            marginBottom: 3,
            textAlign: "left",
            padding: 2,
            width: "100%",
            maxWidth: 450,
            margin: "auto",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              marginBottom: 2,
            }}
          >
            <Box>
              <Typography
                variant="body1"
                sx={{
                  fontWeight: "bold",
                  marginBottom: 1,
                  color: "rgba(255, 255, 255, 0.9)",
                }}
              >
                Hidden Personal Domains
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: "rgba(255, 255, 255, 0.8)" }}
              >
                Exclude contacts from personal email domains (e.g, Gmail,
                Outlook.com) from your network overview. Only business contacts
                will be shown.
              </Typography>
            </Box>
            <IconSwitch
              checked={hiddenPersonalDomains}
              onChange={(e) => setHiddenPersonalDomains(e.target.checked)}
              sx={{ marginLeft: 2 }}
            />
          </Box>
        </Card>

        <Card
          sx={{
            background: "none",
            boxShadow: "none",
            marginBottom: 4,
            textAlign: "left",
            padding: 2,
            width: "100%",
            maxWidth: 450,
            margin: "auto",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
            }}
          >
            <Box>
              <Typography
                variant="body1"
                sx={{
                  fontWeight: "bold",
                  marginBottom: 1,
                  color: "rgba(255, 255, 255, 0.9)",
                }}
              >
                Mark Contacts Private by Default
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: "rgba(255, 255, 255, 0.8)" }}
              >
                Automatically mark newly added contacts as private, requiring
                manual approval for visibility to other team members. Existing
                contacts are unaffected.
              </Typography>
            </Box>
            <IconSwitch
              checked={markContactsPrivate}
              onChange={(e) => setMarkContactsPrivate(e.target.checked)}
              sx={{ marginLeft: 2 }}
            />
          </Box>
        </Card>

        <Box sx={{ display: "flex", gap: 2, justifyContent: "center", mt:2 }}>
          {/* <ActionButton className="back-btn" onClick={handleBack}>
            Back
          </ActionButton> */}
          <ActionButton className="continue-btn" onClick={handleFinish}>
            Finish
          </ActionButton>
        </Box>
      </ContentBox>
    </StepContainer>
  );

  // Render based on current step
  const renderStep = () => {
    switch (currentStep) {
      case 0:
        return renderWelcomeStep();
      case 1:
        return renderEmailStep();
      case 2:
        return renderUploadStep();
      case 3:
        return renderPrivacyStep();
      default:
        return renderWelcomeStep();
    }
  };

  return <Box>{renderStep()}</Box>;
};

export default OnboardingPage;
